import { reactive } from 'vue';
import { useTextStore } from '@/stores/texts';
import type { EditErrorCode, LineLock } from '@/api/types/orderEdit';

/**
 * Every French string of the order-edit page and of the dish-page block
 * (EAUG-77 §9.3): a text-store key Hervé can reword in Directus, with this
 * default. Placeholders ({date}, {amount}…) are filled by fillText.
 */
export const EDIT_TEXT_DEFAULTS = {
  edit_loading: 'Chargement de votre réservation…',
  edit_header_date: 'Votre réservation du {date}',
  edit_header_deadline: "modifiable jusqu'au {deadline}",
  edit_intro: 'Bonjour {firstName}, modifiez ci-dessous les quantités de votre commande.',
  edit_intro_anonymous: 'Bonjour, modifiez ci-dessous les quantités de votre commande.',
  edit_section_menus: 'Menus',
  edit_section_drinks: 'Boissons',
  edit_section_options: 'Options',
  edit_menus_locked_dishes: 'Plats choisis : menus verrouillés',
  edit_menus_locked_capacity: 'Le nombre de menus de cette réservation ne peut pas être modifié en ligne, contactez-nous.',
  edit_capacity_reached: 'Au-delà de {capacity} convives, contactez-nous',
  edit_menus_min: 'Le nombre de menus ne peut pas descendre sous {headcount}.',
  edit_lock_not_offered: 'Non proposé pour votre réservation',
  edit_lock_decrease_only: "Ne peut plus qu'être diminué",
  edit_lock_drinks_add_only: 'Ajout uniquement depuis ce lien',
  edit_lock_price_unavailable: 'Modification en ligne indisponible pour cet article',
  edit_lock_fixed: 'Non modifiable en ligne',
  edit_offered_from: 'À partir de {count} convives',
  edit_line_current: 'Actuellement : {count}',
  edit_summary_title: 'Vos modifications',
  edit_summary_empty: 'Modifiez les quantités ci-dessus pour mettre à jour votre commande.',
  edit_summary_computing: 'Calcul du montant…',
  edit_change_line: '{label} : {from} → {to}',
  edit_to_pay: 'À payer maintenant : {amount}',
  edit_to_refund: 'À rembourser après votre venue : {amount}',
  edit_nothing_due: 'Aucun montant à régler',
  edit_validate: 'Valider',
  edit_confirm_again: 'Confirmer le nouveau montant',
  edit_quote_changed: 'Le montant a changé entre-temps. Vérifiez-le puis confirmez à nouveau.',
  edit_redirecting: 'Redirection vers le paiement…',
  edit_checkout_fallback: 'Continuer vers le paiement',
  edit_rule_line_not_editable: '{label} ne peut pas être modifié en ligne.',
  edit_rule_below_minimum: 'La quantité de {label} est trop basse.',
  edit_rule_above_maximum: 'La quantité de {label} est trop élevée.',
  edit_rule_headcount_decrease: 'Le nombre de menus ne peut pas diminuer.',
  edit_rule_above_capacity: 'Votre salle ne peut pas accueillir autant de convives, contactez-nous.',
  edit_rule_menus_locked: 'Les menus ne peuvent plus être modifiés.',
  edit_rule_family_switch: 'La fondue ne se combine pas avec les autres menus.',
  edit_rule_brunch_invalid: 'Le brunch se commande pour 2 personnes minimum, et un seul brunch par réservation.',
  edit_rule_not_offered: "{label} n'est pas proposé pour votre réservation.",
  edit_rule_no_change: 'Aucune modification à enregistrer.',
  edit_rule_price_unavailable: 'Le prix de {label} est indisponible : cet article ne peut pas être modifié en ligne.',
  edit_status_checking: 'Nous vérifions votre paiement…',
  edit_status_applied: 'Modification confirmée, un e-mail vous a été envoyé',
  edit_status_paid: "Paiement reçu, votre modification est en cours d'enregistrement",
  edit_status_processing: "Votre modification est en cours d'enregistrement, un e-mail vous sera envoyé.",
  edit_status_attention: 'Paiement reçu. Nous finalisons votre modification et revenons vers vous si nécessaire.',
  edit_modification_pending: 'Une modification est en cours de paiement, réessayez dans quelques minutes',
  edit_payment_in_progress: "Une modification précédente vient d'être payée : votre commande a été mise à jour.",
  edit_error_invalid_link: "Ce lien de modification n'est pas valide.",
  edit_error_deadline_passed: 'Le délai pour modifier votre réservation en ligne est dépassé.',
  edit_error_not_eligible: 'Cette réservation ne peut pas être modifiée en ligne.',
  edit_error_disabled: "La modification en ligne n'est pas disponible pour le moment.",
  edit_error_unavailable: 'La modification en ligne est momentanément indisponible, réessayez plus tard.',
  edit_error_too_many: 'Trop de tentatives, réessayez dans une minute.',
  edit_error_generic: 'Une erreur est survenue, réessayez dans quelques instants.',
  edit_contact: 'Contactez-nous :',
  edit_menu_link_question: 'Souhaitez-vous modifier votre réservation avant de faire votre choix de menu ?',
  edit_menu_link_button: 'Recevoir mon lien de modification',
  edit_menu_link_sent: 'Nous vous avons envoyé un lien à {email}',
  edit_menu_link_too_many: 'Plusieurs liens vous ont déjà été envoyés : consultez votre boîte mail ou réessayez dans une heure.',
  edit_menu_link_failed: "L'envoi du lien a échoué, réessayez plus tard.",
  edit_menu_drinks_title: 'Vos boissons',
  edit_menu_drinks_add: 'Ajouter ces boissons',
  edit_menu_info: 'Pour retirer ou remplacer une boisson, ou modifier vos menus et options, demandez votre lien de modification personnel',
  edit_menu_unsaved_dishes: "Enregistrez d'abord votre choix de plats, sinon il sera perdu",
  edit_menu_unsaved_cancel: 'Revenir au choix des plats',
  edit_menu_unsaved_continue: 'Continuer sans enregistrer',
  edit_menu_headcount_title: 'Commande mise à jour',
  edit_menu_headcount_changed: 'Votre commande a été modifiée entre-temps : vous avez maintenant {count} menus. Complétez votre choix de plats puis validez à nouveau.',
  edit_ok: "J'ai compris",
} as const;

export type EditTextKey = keyof typeof EDIT_TEXT_DEFAULTS;
export type EditTexts = Record<EditTextKey, string>;

/** The venue's contact on every error and « contactez-nous » line. */
export const VENUE_CONTACT = {
  email: 'diner@eauget.com',
  phone: '+32 477 73 39 96',
  phoneHref: 'tel:+32477733996',
} as const;

export function fillText(template: string, values: Record<string, string | number>): string {
  return template.replace(/\{(\w+)\}/g, (placeholder, key: string) => (key in values ? String(values[key]) : placeholder));
}

const cache = new WeakMap<object, EditTexts>();

/** The texts, one reactive copy per text store: defaults at once, Directus wording as soon as it is loaded. */
export function useEditTexts(): EditTexts {
  const textStore = useTextStore();
  const cached = cache.get(textStore);
  if (cached) return cached;
  const texts = reactive({ ...EDIT_TEXT_DEFAULTS }) as EditTexts;
  cache.set(textStore, texts);
  void (async () => {
    const all = await textStore.getAll();
    for (const key of Object.keys(EDIT_TEXT_DEFAULTS) as EditTextKey[]) {
      if (all[key]) texts[key] = all[key];
    }
  })();
  return texts;
}

const ERROR_TEXT: Partial<Record<EditErrorCode, EditTextKey>> = {
  invalid_link: 'edit_error_invalid_link',
  deadline_passed: 'edit_error_deadline_passed',
  not_eligible: 'edit_error_not_eligible',
  disabled: 'edit_error_disabled',
  order_edit_unavailable: 'edit_error_unavailable',
  too_many_requests: 'edit_error_too_many',
};

export function editErrorText(t: EditTexts, code: EditErrorCode | null): string {
  const key = code ? ERROR_TEXT[code] : undefined;
  return key ? t[key] : t.edit_error_generic;
}

const LOCK_TEXT: Record<LineLock, EditTextKey> = {
  dishes_chosen: 'edit_menus_locked_dishes',
  capacity_unknown: 'edit_menus_locked_capacity',
  not_offered: 'edit_lock_not_offered',
  decrease_only: 'edit_lock_decrease_only',
  drinks_add_only: 'edit_lock_drinks_add_only',
  price_unavailable: 'edit_lock_price_unavailable',
  fixed: 'edit_lock_fixed',
};

export function lockText(t: EditTexts, lock: LineLock | null): string | null {
  return lock ? t[LOCK_TEXT[lock]] : null;
}

const RULE_TEXT: Partial<Record<EditErrorCode, EditTextKey>> = {
  line_not_editable: 'edit_rule_line_not_editable',
  below_minimum: 'edit_rule_below_minimum',
  above_maximum: 'edit_rule_above_maximum',
  headcount_decrease: 'edit_rule_headcount_decrease',
  above_capacity: 'edit_rule_above_capacity',
  menus_locked: 'edit_rule_menus_locked',
  family_switch: 'edit_rule_family_switch',
  brunch_invalid: 'edit_rule_brunch_invalid',
  not_offered: 'edit_rule_not_offered',
  no_change: 'edit_rule_no_change',
  price_unavailable: 'edit_rule_price_unavailable',
};

/** A 422 the server answered for one product, in French. */
export function ruleErrorText(t: EditTexts, code: EditErrorCode, label: string): string {
  const key = RULE_TEXT[code];
  return key ? fillText(t[key], { label }) : t.edit_error_generic;
}
